import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { TlkCardComponent } from '@itero/ui-toolkit-angular/card';
import { TranslateModule } from '@ngx-translate/core';
import { BaseDestroyableComponent } from '@shared/base-classes/base-destroyable';
import { RxProcedureTypeControlComponent } from '@shared/components/rx-procedure-type-control/rx-procedure-type-control.component';
import { SharedModule } from '@shared/shared.module';
import { Observable } from 'rxjs';
import { shareReplay, skip, takeUntil, tap } from 'rxjs/operators';
import { IdName } from '@shared/models/id-name';
import { RxIntuitiveInvisalignDetailsFacade } from '../../rx-intuitive-invisalign-details.facade';
import { OrderModule } from '@modules/order/order.module';
import { FormControlStatus } from '@shared/models/form-control-status';
import { TlkIncrementalNumberInputComponent } from '@itero/ui-toolkit-angular/incremental-number-input';
import { TlkTextInputComponent } from '@itero/ui-toolkit-angular/text-input';
import { maxAlignerNumberValue } from '@shared/models/consts';

@Component({
	selector: 'rx-intuitive-invisalign-details',
	templateUrl: './rx-intuitive-invisalign-details.component.html',
	styleUrls: ['./rx-intuitive-invisalign-details.component.scss'],
	providers: [RxIntuitiveInvisalignDetailsFacade],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [
		CommonModule,
		ReactiveFormsModule,
		SharedModule,
		TranslateModule,
		TlkCardComponent,
		RxProcedureTypeControlComponent,
		OrderModule,
		TlkIncrementalNumberInputComponent,
		TlkTextInputComponent
	]
})
export class RxIntuitiveInvisalignDetailsComponent extends BaseDestroyableComponent implements OnInit {
	private isReadOnly: boolean;

	isReadOnly$: Observable<boolean> = this.facade.isReadOnly$.pipe(
		tap(isReadOnly => (this.isReadOnly = isReadOnly)),
		shareReplay({ refCount: true, bufferSize: 1 })
	);

	// Individual field states
	treatmentStageState$: Observable<FormControlStatus> = this.facade.treatmentStageState$;
	currentAlignerIdState$: Observable<FormControlStatus> = this.facade.currentAlignerIdState$;

	procedureTreatmentStages$ = this.facade.procedureTreatmentStages$;
	procedureTitle$ = this.facade.procedureTitle$;

	form = this.formBuilder.group({
		treatmentStage: null as IdName | null,
		currentAlignerId: [null as number | null, [Validators.min(0), Validators.max(this.maxAlignerNumberValue)]]
	});

	get treatmentStageControl() {
		return this.form.controls.treatmentStage;
	}

	get hasTreatmentStageValue() {
		return this.treatmentStageControl?.value !== this.treatmentStageControl?.defaultValue;
	}

	get currentAlignerIdControl() {
		return this.form.controls.currentAlignerId;
	}

	get maxAlignerNumberValue() {
		return maxAlignerNumberValue;
	}

	constructor(
		private readonly facade: RxIntuitiveInvisalignDetailsFacade,
		private readonly formBuilder: FormBuilder
	) {
		super();
	}

	ngOnInit(): void {
		this.loadFromStore();
		this.subscribeToRxLoaded();
		this.subscribeToUserChanges();
		this.subscribeToStoreChanges();
		this.subscribeToStateChanges();
	}

	clearControl(control: FormControl) {
		if (!this.isReadOnly) {
			control.patchValue(null);
		}
	}

	private loadFromStore(): void {
		const { treatmentStage, currentAlignerId } = this.facade.storeState;

		this.form.patchValue(
			{
				treatmentStage,
				currentAlignerId: currentAlignerId ? Number(currentAlignerId) : null
			},
			{ emitEvent: false }
		);
	}

	private subscribeToRxLoaded(): void {
		this.facade
			.getLoadedRx()
			.pipe(
				tap(state => {
					this.form.patchValue(
						{
							treatmentStage: state.treatmentStage,
							currentAlignerId: state.currentAlignerId ? Number(state.currentAlignerId) : null
						},
						{ emitEvent: false }
					);

					// Check if currentAlignerId needs to be set to '0' for non-InitialRecord
					// ProcedureLoadRxService already populated OrderStore, but may need correction
					if (state.treatmentStage) {
						this.facade.handleTreatmentStageChanged(state.treatmentStage);
					}
				}),
				takeUntil(this.componentAlive$)
			)
			.subscribe();
	}

	private subscribeToUserChanges(): void {
		this.treatmentStageControl.valueChanges
			.pipe(
				tap(stage => this.facade.handleTreatmentStageChanged(stage)),
				takeUntil(this.componentAlive$)
			)
			.subscribe();

		this.currentAlignerIdControl.valueChanges
			.pipe(
				tap(value => {
					const stringValue = value !== null ? String(value) : null;

					this.facade.handleCurrentAlignerChanged(stringValue);
				}),
				takeUntil(this.componentAlive$)
			)
			.subscribe();
	}

	private subscribeToStoreChanges(): void {
		this.facade.currentAlignerId$.pipe(skip(1), takeUntil(this.componentAlive$)).subscribe(currentAlignerId => {
			const numValue = currentAlignerId ? Number(currentAlignerId) : null;

			this.currentAlignerIdControl.patchValue(numValue, { emitEvent: false });
		});
	}

	private subscribeToStateChanges(): void {
		this.treatmentStageState$
			.pipe(
				skip(1), // Skip initial value
				takeUntil(this.componentAlive$)
			)
			.subscribe(state => {
				// When treatment stage becomes disabled (hidden), clear its value
				if (!state.isEnabled && this.treatmentStageControl.value !== null) {
					this.treatmentStageControl.patchValue(null);
				}
			});
	}
}
